/* assets/a11y-cart.js
 * Accessible cart confirmation dialog + live announcements
 * - Focus shifts to main heading on open
 * - Works with forms, fetch, XHR, and custom events
 */
(() => {
  // ---------- 0) Ensure a global live region exists (top of <body> ideally) ----------
  let live = document.getElementById('sr-updates');
  function ensureLiveRegion() {
    if (!live) {
      live = document.createElement('div');
      live.id = 'sr-updates';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'assertive');
      live.setAttribute('aria-atomic', 'true');
      live.style.position = 'absolute';
      live.style.width = '1px';
      live.style.height = '1px';
      live.style.padding = '0';
      live.style.margin = '-1px';
      live.style.overflow = 'hidden';
      live.style.clip = 'rect(0,0,0,0)';
      live.style.whiteSpace = 'nowrap';
      live.style.border = '0';
      document.body.prepend(live);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureLiveRegion);
  } else {
    ensureLiveRegion();
  }

  // Optional: very soft earcon (beep) after user gesture (autoplay policies)
  let playEarcon = (() => {
    let ctx = null;
    return () => {
      try {
        if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880; // A5
        gain.gain.value = 0.0001;
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start();
        const t = ctx.currentTime;
        gain.gain.exponentialRampToValueAtTime(0.02, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
        osc.stop(t + 0.09);
      } catch {}
    };
  })();

  // ---------- 1) Cache dialog + heading ----------
  const dialog  = document.getElementById('purchase-confirmation-dialog');
  const heading = document.getElementById('purchase-confirmation-title');
  const closeBtn = dialog?.querySelector('[data-purchase-confirmation-close]');

  // Fallback: ensure dialog semantics exist even if HTML missed them
  if (dialog) {
    if (!dialog.hasAttribute('role')) dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
  }
  if (heading && !heading.hasAttribute('tabindex')) {
    heading.setAttribute('tabindex', '-1'); // allow programmatic focus
  }

  // ---------- 2) Focus helpers ----------
  const FOCUSABLE_SEL =
    'a[href], area[href], button:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, ' +
    '[tabindex]:not([tabindex="-1"])';

  const getFocusable = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE_SEL))
      .filter(el => el.offsetParent !== null || el === root);

  const isVisible = (el) => !!el && el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';

  let lastFocused = null;
  let removeTrap = null;

  function trapFocus(container, onEscape) {
    function handler(e) {
      if (e.key === 'Escape') { e.preventDefault(); onEscape(); return; }
      if (e.key !== 'Tab') return;
      const items = getFocusable(container);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  // Simple background "inert" using aria-hidden (safe across themes)
  const siblingsOf = (el) => Array.from(document.body.children).filter(n => n !== el);
  function setBackgroundHidden(hidden) {
    if (!dialog) return;
    siblingsOf(dialog).forEach(n => {
      if (hidden) {
        if (!n.hasAttribute('aria-hidden')) n.dataset.prevAriaHidden = 'none';
        n.setAttribute('aria-hidden', 'true');
      } else {
        if (n.dataset.prevAriaHidden === 'none') n.removeAttribute('aria-hidden');
        delete n.dataset.prevAriaHidden;
      }
    });
  }

  // ---------- 3) Open/close behavior (focus -> HEADING) ----------
  function focusHeadingWhenVisible() {
    if (heading && isVisible(dialog)) {
      heading.focus({ preventScroll: true });
      return true;
    }
    return false;
  }

  function openDialog() {
    if (!dialog) return;
    dialog.removeAttribute('hidden');

    lastFocused = document.activeElement;
    // Trap focus & set background hidden
    removeTrap = trapFocus(dialog, closeDialog);
    setBackgroundHidden(true);
    document.querySelectorAll('[aria-controls="purchase-confirmation-dialog"]').forEach(o => o.setAttribute('aria-expanded', 'true'));

    // Move focus to main heading even if there is an opening animation
    if (!focusHeadingWhenVisible()) {
      requestAnimationFrame(() => {
        if (!focusHeadingWhenVisible()) {
          setTimeout(focusHeadingWhenVisible, 120);
        }
      });
    }
  }

  function closeDialog() {
    if (!dialog) return;
    removeTrap && removeTrap();
    dialog.setAttribute('hidden', '');
    setBackgroundHidden(false);
    document.querySelectorAll('[aria-controls="purchase-confirmation-dialog"]').forEach(o => o.setAttribute('aria-expanded', 'false'));
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  closeBtn?.addEventListener('click', closeDialog);
  dialog?.addEventListener('mousedown', (e) => { if (e.target === dialog) closeDialog(); });

  // ---------- 4) Announce helper ----------
  async function getCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      const j = await res.json();
      return j.item_count | 0;
    } catch { return null; }
  }

  async function announceAdded({ title = 'Item', quantity = 1 }) {
    const count = await getCartCount();
    const msg = `${quantity} ${title} added to cart.${count != null ? ` Your cart now has ${count} item${count === 1 ? '' : 's'}.` : ''}`;

    // Force re-announce by clearing then setting after a tick
    if (live) {
      live.textContent = '';
      setTimeout(() => { live.textContent = msg; }, 20);
    }

    playEarcon(); // optional small beep

    // Open dialog and KEEP focus on the heading (don’t shift to Checkout)
    openDialog();
  }

  // Expose for other scripts
  window.announceAddToCart = announceAdded;

  // ---------- 5) Intercept ALL add-to-cart paths ----------

  // (a) Forms that post to /cart/add
  document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      const submitter = e.submitter;
      try {
        e.preventDefault();
        const fd = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd
        });
        if (!res.ok) throw new Error('Add to cart failed');
        const added = await res.json();
        await announceAdded({ title: added.title, quantity: Number(added.quantity) || 1 });
      } catch (err) {
        live && (live.textContent = 'There was a problem adding the item to the cart.');
        (submitter || form).focus();
        console.error(err);
      }
    }, { capture: true });
  });

  // (b) fetch('/cart/add.js') or fetch('/cart/change.js')
  const _fetch = window.fetch.bind(window);
  window.fetch = async function(input, init) {
    const res = await _fetch(input, init);
    try {
      const url = typeof input === 'string' ? input : (input?.url || '');
      const method = (init?.method || 'GET').toUpperCase();
      if (method !== 'GET' && res.ok && (url.includes('/cart/add') || url.includes('/cart/change'))) {
        res.clone().json().then(j => {
          const title = j?.title || j?.items?.[0]?.title || 'Item';
          const qty = Number(j?.quantity) || Number(j?.items?.[0]?.quantity) || 1;
          announceAdded({ title, quantity: qty });
        }).catch(()=>{});
      }
    } catch {}
    return res;
  };

  // (c) Legacy XMLHttpRequest paths
  (function patchXHR() {
    const XHR = window.XMLHttpRequest;
    if (!XHR) return;
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;

    XHR.prototype.open = function(method, url, ...rest) {
      this.__a11y_url = (typeof url === 'string' ? url : (url?.toString?.() || ''));
      this.__a11y_method = (method || 'GET').toUpperCase();
      return open.call(this, method, url, ...rest);
    };

    XHR.prototype.send = function(body) {
      this.addEventListener('load', function() {
        try {
          const url = this.__a11y_url || '';
          const method = this.__a11y_method || 'GET';
          if (this.status >= 200 && this.status < 300 && method !== 'GET' && (url.includes('/cart/add') || url.includes('/cart/change'))) {
            const ct = this.getResponseHeader('Content-Type') || '';
            if (ct.includes('application/json')) {
              const j = JSON.parse(this.responseText || '{}');
              const title = j?.title || j?.items?.[0]?.title || 'Item';
              const qty = Number(j?.quantity) || Number(j?.items?.[0]?.quantity) || 1;
              announceAdded({ title, quantity: qty });
            } else {
              announceAdded({ title: 'Item', quantity: 1 });
            }
          }
        } catch {}
      });
      return send.call(this, body);
    };
  })();

  // (d) Shopify global callback (if present)
  if (window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
    const prev = window.Shopify.onItemAdded;
    window.Shopify.onItemAdded = function(lineItem, ...rest) {
      announceAdded({ title: lineItem?.title || 'Item', quantity: Number(lineItem?.quantity) || 1 });
      return prev.apply(this, [lineItem, ...rest]);
    };
  }

  // (e) Custom theme events
  document.addEventListener('cart:added', (e) => {
    const d = e.detail || {};
    announceAdded({ title: d.title || 'Item', quantity: Number(d.quantity) || 1 });
  });
  document.addEventListener('product:added', (e) => {
    const d = e.detail || {};
    announceAdded({ title: d.title || 'Item', quantity: Number(d.quantity) || 1 });
  });

  // (f) Optional: opener buttons
  document.querySelectorAll('[aria-controls="purchase-confirmation-dialog"]').forEach(btn => {
    btn.addEventListener('click', (e) => { e.preventDefault(); openDialog(); });
  });
})();

// ===== Quantity controls: normalize to ONE action per key press/click =====
(() => {
  const WRAPPER_SEL = '[data-qty-wrapper]';
  const INC_SEL = '[data-qty="inc"]';
  const DEC_SEL = '[data-qty="dec"]';

  // Find input by aria-controls. Fall back to sibling input.
  function getLinkedInput(btn) {
    const id = btn.getAttribute('aria-controls');
    if (id) return document.getElementById(id);
    return btn.closest(WRAPPER_SEL)?.querySelector('input[type="number"], .qty__input');
  }

  function clamp(val, min, max) {
    if (min != null) val = Math.max(val, min);
    if (max != null && isFinite(max)) val = Math.min(val, max);
    return val;
  }

  function adjust(input, delta) {
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = input.min === '' ? null : Number(input.min);
    const max = input.max === '' ? null : Number(input.max);
    const current = Number(input.value || 0);
    const next = clamp(current + delta * step, min, max);
    if (next === current) return;

    input.value = String(next);
    // Fire the usual events so your cart JS updates totals/drawer, etc.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // IMPORTANT:
  //  - We do NOT change quantity in keydown.
  //  - We only preventDefault for Space/Enter in keydown (to avoid scrolling/quirks).
  //  - We perform the change on 'click' only.
  function onKeydown(e) {
    if (e.key === ' ' || e.key === 'Enter') {
      // Prevent the spacebar from scrolling and duplicate handlers from other scripts.
      e.preventDefault();
      // Do NOT change quantity here. Browsers/screen readers will fire a CLICK next.
    }
  }

  function onClick(e) {
    const incBtn = e.target.closest(INC_SEL);
    const decBtn = e.target.closest(DEC_SEL);
    if (!incBtn && !decBtn) return;

    e.preventDefault();
    const input = getLinkedInput(incBtn || decBtn);
    adjust(input, incBtn ? +1 : -1);
  }

  // Use event delegation so newly added line items work automatically.
  document.addEventListener('keydown', function(e) {
    if (e.target.closest(INC_SEL) || e.target.closest(DEC_SEL)) onKeydown(e);
  }, true); // capture=true helps intercept before any theme key handlers

  document.addEventListener('click', onClick);

  // (Optional) If your theme had custom key handlers that already change quantity,
  // this capture-phase handler and the “do nothing on keydown” rule will stop the double bump.
})();